import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Checkbox,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import Breadcrumb from '../components/Breadcrumb';

const RegistrationsView = () => {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [pendingUpdates, setPendingUpdates] = useState({});
  const pendingUpdatesRef = useRef({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const [error, setError] = useState('');

  const labels = {
    telugu: {
      title: 'సభ్యుల నమోదు నివేదిక',
      name: 'పేరు',
      mobile: 'మొబైల్',
      occupation: 'వృత్తి',
      category: 'కేటగిరీ',
      date: 'తేదీ',
      loading: 'లోడవుతోంది...',
      noData: 'డేటా లేదు',
      filterAll: 'అన్ని రికార్డులు',
      filterEndorsed: 'కమిటీ ఆమోదిత సభ్యులు',
    },
    sanskrit: {
      title: 'सदस्य पञ्जीकरण నివేదిక',
      name: 'नाम',
      mobile: 'चलवाणी',
      occupation: 'वृत्तिः',
      category: 'श्रेणी',
      date: 'दिनाङ्कః',
      loading: 'आरोप्यते...',
      noData: 'दत्तांशః नास्ति',
      filterAll: 'सर्व अभिलेखाः',
      filterEndorsed: 'समिति अनुमोदित सदस्याः',
    }
  };

  const t = labels[lang] || labels.telugu;

  const buildTree = (memberList) => {
    const map = {};
    memberList.forEach(m => {
      map[m._id] = { ...m, children: [] };
    });
    const roots = [];
    memberList.forEach(m => {
      const parentId = m.referralMemberId?._id || m.referralMemberId;
      if (parentId && map[parentId]) {
        map[parentId].children.push(map[m._id]);
      } else {
        roots.push(map[m._id]);
      }
    });
    return roots;
  };

  const filteredMembers = useMemo(() => {
    if (filter === 'endorsed') {
      return members.filter(m => m.isCommitteeEndorsed === 'Yes');
    }
    return members;
  }, [members, filter]);

  const treeData = useMemo(() => buildTree(filteredMembers), [filteredMembers]);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Keep user ref in sync to avoid effect dependency issues during unmount
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Keep ref in sync with state for unmount cleanup
  useEffect(() => {
    pendingUpdatesRef.current = pendingUpdates;
  }, [pendingUpdates]);

  const savePendingUpdates = () => {
    const updates = pendingUpdatesRef.current;
    const idsToUpdate = Object.keys(updates).filter(id => updates[id]);
    if (idsToUpdate.length > 0) {
      // Clear the ref immediately to prevent double-firing (unmount + beforeunload)
      pendingUpdatesRef.current = {};
      api.post('/api/startup-members/bulk-update-registered-by', {
        memberIds: idsToUpdate,
        registeredBy: userRef.current?.firstName || 'Unknown'
      }).catch(err => console.error('Auto-save failed:', err));
    }
  };

  useEffect(() => {
    window.addEventListener('beforeunload', savePendingUpdates);
    return () => {
      window.removeEventListener('beforeunload', savePendingUpdates);
      savePendingUpdates();
    };
  }, []);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        // Fetch all members (limit=0) to ensure the tree structure is built correctly
        const res = await api.get('/api/startup-members?limit=0');
        const data = await res.json();
        if (res.ok && data.data) {
          const flatData = data.data;
          setMembers(flatData);
          
          // Default expand roots
          const initialRoots = buildTree(flatData);
          const initialExpanded = {};
          initialRoots.forEach(root => { initialExpanded[root._id] = true; });
          setExpanded(initialExpanded);
        } else {
          setError('Failed to fetch members');
        }
      } catch (err) {
        console.error('Error fetching members:', err);
        setError('Network error');
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, []);

  const toggleNode = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAll = (expand) => {
    const newExpanded = {};
    if (expand) {
      members.forEach(m => { newExpanded[m._id] = true; });
    }
    setExpanded(newExpanded);
  };

  // Calculate summary of registrations by user
  const getRegistrationStats = () => {
    const stats = members.reduce((acc, m) => {
      if (m.registeredBy) acc[m.registeredBy] = (acc[m.registeredBy] || 0) + 1;
      return acc;
    }, {});

    const pendingCount = Object.values(pendingUpdates).filter(Boolean).length;
    if (pendingCount > 0) {
      const currentUserName = user?.firstName || 'Unknown';
      stats[currentUserName] = (stats[currentUserName] || 0) + pendingCount;
    }
    return Object.entries(stats).sort((a, b) => b[1] - a[1]);
  };

  const handleCheck = (id, checked) => {
    setPendingUpdates(prev => ({
      ...prev,
      [id]: checked
    }));
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Platinum': return { bg: '#E5E4E2', text: '#333' };
      case 'Gold': return { bg: '#FFD700', text: '#000' };
      case 'Silver': return { bg: '#C0C0C0', text: '#000' };
      case 'Bronze': return { bg: '#CD7F32', text: '#fff' };
      default: return { bg: '#f5f5f5', text: '#666' };
    }
  };

  // Recursive function to count all descendants
  const countDescendants = (member) => {
    let count = member.children.length;
    member.children.forEach(child => {
      count += countDescendants(child);
    });
    return count;
  };

  const MemberNode = ({ member, depth = 0 }) => {
    const hasChildren = member.children && member.children.length > 0;
    const isExpanded = expanded[member._id];
    const catStyle = getCategoryColor(member.category);

    return (
      <Box sx={{ 
        ml: depth === 0 ? 0 : { xs: 1.5, sm: 3, md: 4 }, 
        position: 'relative' 
      }}>
        {depth > 0 && (
          <Box sx={{
            position: 'absolute',
            left: { xs: -12, sm: -24, md: -32 },
            top: 24,
            width: { xs: 12, sm: 24, md: 32 },
            height: 1.5,
            bgcolor: '#7f4b2d30'
          }} />
        )}
        <Card variant="outlined" sx={{ 
          mb: 1, 
          maxWidth: { xs: '100%', md: 600 }, 
          borderLeft: `5px solid ${catStyle.bg}`,
          transition: 'all 0.2s',
          '&:hover': { boxShadow: 2, borderColor: '#7f4b2d' }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', p: 0.5 }}>
            <IconButton 
              size="small" 
              onClick={() => toggleNode(member._id)}
              sx={{ 
                visibility: hasChildren ? 'visible' : 'hidden',
                mt: 0.5 
              }}
            >
              {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
            <CardContent sx={{ flex: 1, py: '8px !important', px: 1, minWidth: 0 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 0.5,
                mb: 0.5
              }}>
                <Typography variant="subtitle1" sx={{ 
                  fontWeight: 700, 
                  color: '#7f4b2d',
                  fontSize: { xs: '0.85rem', sm: '1rem' },
                  lineHeight: 1.2
                }}>
                  {member.fullNameWithoutSurname} {member.surname} 
                  {hasChildren && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', fontWeight: 500 }}>
                      ({countDescendants(member)})
                    </Typography>
                  )}
                </Typography>
                <Chip 
                  label={member.category} 
                  size="small" 
                  sx={{ 
                    backgroundColor: catStyle.bg, 
                    color: catStyle.text, 
                    fontWeight: 800, 
                    fontSize: '0.65rem',
                    height: 20
                  }} 
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1
              }}>
                <span>📞 {member.mobileNumber}</span>
                <span style={{ color: '#ccc', display: { xs: 'none', sm: 'inline' } }}>|</span>
                <span>💼 {Array.isArray(member.occupation) ? member.occupation.join(', ') : member.occupation}</span>
              </Typography>
            </CardContent>
            {!member.registeredBy && (
              <Checkbox
                checked={!!pendingUpdates[member._id]}
                onChange={(e) => handleCheck(member._id, e.target.checked)}
                size="small"
                sx={{ mt: 0.5, color: '#7f4b2d' }}
              />
            )}
          </Box>
        </Card>
        <Collapse in={isExpanded}>
          {member.children.map(child => (
            <MemberNode key={child._id} member={child} depth={depth + 1} />
          ))}
        </Collapse>
      </Box>
    );
  };

  return (
    <Container maxWidth="lg">
      <Breadcrumb
        items={[
          { label: lang === 'telugu' ? 'సభ్యుల నమోదు విభాగం' : 'सदस्य पञ्जीकरण विभागः', path: '/member-management' },
          { label: t.title }
        ]}
      />
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AccountTreeIcon sx={{ fontSize: 40, color: '#7f4b2d' }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#7f4b2d' }}>
              {t.title}
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={1}>
            <Tooltip title={lang === 'telugu' ? 'వడపోతలు' : 'शोधकाః'}>
              <IconButton onClick={handleMenuOpen} color="primary" sx={{ bgcolor: '#7f4b2d10' }}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose}>
              <MenuItem onClick={() => { toggleAll(true); handleMenuClose(); }}>
                <UnfoldMoreIcon sx={{ mr: 1, fontSize: 20 }} />
                {lang === 'telugu' ? 'అన్నీ చూపించు' : 'సర్వం విస్తారయతు'}
              </MenuItem>
              <MenuItem onClick={() => { toggleAll(false); handleMenuClose(); }}>
                <UnfoldLessIcon sx={{ mr: 1, fontSize: 20 }} />
                {lang === 'telugu' ? 'అన్నీ దాచు' : 'సర్వం సంకోచయతు'}
              </MenuItem>
              <Divider />
              <MenuItem 
                onClick={() => { setFilter('all'); handleMenuClose(); }} 
                selected={filter === 'all'}
              >
                {t.filterAll}
              </MenuItem>
              <MenuItem 
                onClick={() => { setFilter('endorsed'); handleMenuClose(); }} 
                selected={filter === 'endorsed'}
              >
                {t.filterEndorsed}
              </MenuItem>
            </Menu>
          </Stack>
        </Box>

        {/* Summary of Registrations */}
        {!loading && members.length > 0 && (
          <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 1, px: 1 }}>
            {getRegistrationStats().map(([name, count]) => (
              <Chip
                key={name}
                label={`${name}: ${count}`}
                variant="outlined"
                size="small"
                sx={{ 
                  fontWeight: 700, 
                  color: '#7f4b2d', 
                  borderColor: '#7f4b2d40',
                  backgroundColor: '#7f4b2d08'
                }}
              />
            ))}
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#fff8ed', 
            borderRadius: 3, 
            minHeight: 400,
            border: '1px solid #7f4b2d10'
          }}>
            {treeData.length === 0 ? (
              <Typography align="center" sx={{ py: 4, color: 'text.secondary' }}>{t.noData}</Typography>
            ) : (
              treeData.map(root => (
                <MemberNode key={root._id} member={root} />
              ))
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default RegistrationsView;